import api from './api';

export interface ClientContact {
  id: number;
  last_name: string;
  first_name: string;
  patronymic: string | null;
  position: string | null;
  phone: string | null;
  email: string | null;
  birth_date: string | null;
  note: string | null;
}

// Тип для контактов при создании/обновлении (id может быть undefined для новых контактов)
export interface ClientContactInput {
  id?: number;
  first_name: string;
  last_name: string;
  patronymic: string | null;
  position: string | null;
  phone: string | null;
  email: string | null;
  birth_date: string | null;
  note: string | null;
}

// Тип для данных создания/обновления клиента
export type ClientInput = Omit<Partial<Client>, 'contacts'> & {
  client_type: 'individual' | 'legal';
  contacts?: ClientContactInput[];
};

export type ClientUpdateInput = Omit<Partial<Client>, 'contacts'> & {
  client_type?: 'individual' | 'legal';
  contacts?: ClientContactInput[];
};

export interface Client {
  id: number;
  name: string;
  tag?: string | null;
  client_type?: 'individual' | 'legal';
  inn?: string | null;
  legal_name?: string | null;
  legal_address?: string | null;
  phone?: string | null;
  email?: string | null;
  birth_date?: string | null;
  is_archived?: boolean;
  archived_at?: string | null;
  curator: string | null;
  curator_id: number | null;
  field_of_activity: string | null;
  projects: number;
  cases: number;
  tasks: number;
  cooperation_year: number | null;
  cooperation_date?: string | null; // YYYY-MM-DD
  note?: string | null;
  contacts?: ClientContact[];
}

export interface ClientsResponse {
  data: Client[];
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

export interface ClientsStatistics {
  total_clients: number;
  clients_this_month: number;
}

export interface FilterOptions {
  client_names: string[];
  curators: string[];
  fields_of_activity: string[];
  years: number[];
}

export interface ClientsFilterOptionsParams {
  archived?: boolean;
}

export interface ClientsFilters {
  client?: string;
  client_search?: string;
  curator?: string;
  field_of_activity?: string;
  without_tasks?: boolean;
  year?: string;
  archived?: boolean;
  page?: number;
  per_page?: number;
}

function parseFilenameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      return utf8[1].trim();
    }
  }
  const simple = /filename="([^"]+)"/i.exec(header) || /filename=([^;\s]+)/i.exec(header);
  return simple?.[1]?.trim() ?? null;
}

export const clientsService = {
  /**
   * Получить список клиентов с фильтрацией и пагинацией
   */
  async getClients(filters: ClientsFilters = {}): Promise<ClientsResponse> {
    const params = new URLSearchParams();
    
    if (filters.client) params.append('client', filters.client);
    if (filters.client_search) params.append('client_search', filters.client_search);
    if (filters.curator) params.append('curator', filters.curator);
    if (filters.field_of_activity) params.append('field_of_activity', filters.field_of_activity);
    if (filters.without_tasks) params.append('without_tasks', '1');
    if (filters.year) params.append('year', filters.year);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());

    const { data } = await api.get<ClientsResponse>(`/clients?${params.toString()}`);
    return data;
  },

  /**
   * Получить статистику по клиентам
   */
  async getStatistics(): Promise<ClientsStatistics> {
    const { data } = await api.get<ClientsStatistics>('/clients/statistics');
    return data;
  },

  /**
   * Получить опции для фильтров
   */
  async getFilterOptions(params: ClientsFilterOptionsParams = {}): Promise<FilterOptions> {
    const sp = new URLSearchParams();
    if (params.archived) sp.append('archived', '1');
    const urlPath = `/clients/filter-options${sp.toString() ? `?${sp.toString()}` : ''}`;
    const { data } = await api.get<FilterOptions>(urlPath);
    return data;
  },

  /**
   * Получить список архивных клиентов с фильтрацией и пагинацией
   */
  async getClientsArchive(filters: ClientsFilters = {}): Promise<ClientsResponse> {
    const params = new URLSearchParams();

    if (filters.client) params.append('client', filters.client);
    if (filters.client_search) params.append('client_search', filters.client_search);
    if (filters.curator) params.append('curator', filters.curator);
    if (filters.field_of_activity) params.append('field_of_activity', filters.field_of_activity);
    if (filters.without_tasks) params.append('without_tasks', '1');
    if (filters.year) params.append('year', filters.year);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());

    const { data } = await api.get<ClientsResponse>(`/clients/archive?${params.toString()}`);
    return data;
  },

  /**
   * Получить клиента по ID
   */
  async getClient(id: number): Promise<Client> {
    const { data } = await api.get<Client>(`/clients/${id}`);
    return data;
  },

  /**
   * Создать нового клиента
   */
  async createClient(clientData: ClientInput): Promise<Client> {
    const { data } = await api.post<{ data: Client }>('/clients', clientData);
    return data.data;
  },

  /**
   * Обновить клиента
   */
  async updateClient(id: number, clientData: ClientUpdateInput): Promise<Client> {
    const { data } = await api.put<{ data: Client }>(`/clients/${id}`, clientData);
    return data.data;
  },

  /**
   * Удалить клиента
   */
  async deleteClient(id: number): Promise<void> {
    await api.delete(`/clients/${id}`);
  },

  /**
   * Скачать Excel по карточке клиента (GET, blob). При ошибке API (JSON) бросает Error с message.
   */
  async downloadClientExcel(clientId: number): Promise<void> {
    const res = await api.get<Blob>(`/clients/${clientId}/export/excel`, {
      responseType: 'blob',
    });

    const blob = res.data;
    const ctype = String(res.headers['content-type'] || '');
    if (ctype.includes('application/json')) {
      const text = await blob.text();
      let msg = 'Не удалось сформировать Excel';
      try {
        const j = JSON.parse(text) as { message?: string };
        if (j?.message) msg = j.message;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }

    const name = parseFilenameFromContentDisposition(res.headers['content-disposition']) || `client-${clientId}.xlsx`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  /**
   * Скачать Excel по списку клиентов (GET, blob). Параметры фильтров — как в getClients.
   */
  async downloadClientsExcel(filters: Omit<ClientsFilters, 'page' | 'per_page'> = {}): Promise<void> {
    const params = new URLSearchParams();
    if (filters.client) params.append('client', filters.client);
    if (filters.curator) params.append('curator', filters.curator);
    if (filters.field_of_activity) params.append('field_of_activity', filters.field_of_activity);
    if (filters.without_tasks) params.append('without_tasks', '1');
    if (filters.year) params.append('year', filters.year);
    if (filters.archived) params.append('archived', '1');

    const urlPath = `/clients/export/excel${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await api.get<Blob>(urlPath, { responseType: 'blob' });

    const blob = res.data;
    const ctype = String(res.headers['content-type'] || '');
    if (ctype.includes('application/json')) {
      const text = await blob.text();
      let msg = 'Не удалось сформировать Excel';
      try {
        const j = JSON.parse(text) as { message?: string };
        if (j?.message) msg = j.message;
      } catch {
        /* ignore */
      }
      throw new Error(msg);
    }

    const name = parseFilenameFromContentDisposition(res.headers['content-disposition']) || 'clients.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
